import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { projectApi } from '../services/api';
import { cn } from '../utils/cn';

const categoryMap: Record<string, { zh: string; vi: string; color: string }> = {
  electrical:    { zh: '电气工程',  vi: 'Điện công nghiệp',   color: 'bg-blue-100 text-blue-800' },
  hvac:          { zh: '暖通空调',  vi: 'HVAC',                color: 'bg-green-100 text-green-800' },
  ventilation:   { zh: '通风系统',  vi: 'Thông gió',           color: 'bg-purple-100 text-purple-800' },
  decoration:    { zh: '建筑装饰',  vi: 'Trang trí kiến trúc', color: 'bg-yellow-100 text-yellow-800' },
  waterproofing: { zh: '防水地坪',  vi: 'Chống thấm sàn',      color: 'bg-red-100 text-red-800' },
  renovation:    { zh: '改造工程',  vi: 'Cải tạo',             color: 'bg-indigo-100 text-indigo-800' },
};

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [activeImage, setActiveImage] = useState<string>('');

  useEffect(() => {
    if (!id) return;

    const fetchProject = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await projectApi.getProjectById(Number(id));
        if (res.success && res.data) {
          setProject(res.data);

          // 解析封面和图集
          const cover = res.data.cover_image_url || '';
          let gallery: string[] = [];
          if (res.data.gallery_images) {
            try {
              gallery = JSON.parse(res.data.gallery_images);
            } catch {
              gallery = [];
            }
          }
          const allImages = [cover, ...gallery].filter(Boolean);
          setGalleryImages(allImages);
          setActiveImage(allImages[0] || '');
        } else {
          setError(language === 'zh' ? '项目不存在' : 'Dự án không tồn tại');
        }
      } catch {
        setError(language === 'zh' ? '加载项目失败' : 'Tải dự án thất bại');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">{language === 'zh' ? '加载中...' : 'Đang tải...'}</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-700 mb-4">{error || '项目不存在'}</h2>
          <Link to="/projects" className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            {language === 'zh' ? '返回项目列表' : 'Quay lại danh sách'}
          </Link>
        </div>
      </div>
    );
  }

  const cat = categoryMap[project.project_type || ''] || { zh: '其他', vi: 'Khác', color: 'bg-gray-100 text-gray-800' };
  const categoryLabel = language === 'zh' ? cat.zh : cat.vi;
  const year = project.end_date ? new Date(project.end_date).getFullYear() : null;
  const location = language === 'zh'
    ? (project.location_zh || '')
    : (project.location_vi || project.location_zh || '');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 面包屑 */}
      <div className="bg-white border-b py-3">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-blue-600 transition-colors">
              {language === 'zh' ? '首页' : 'Trang chủ'}
            </Link>
            <span>/</span>
            <Link to="/projects" className="hover:text-blue-600 transition-colors">
              {language === 'zh' ? '项目案例' : 'Dự án'}
            </Link>
            <span>/</span>
            <span className="text-gray-800 truncate max-w-xs">
              {language === 'zh' ? project.name_zh : project.name_vi}
            </span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* 左侧：图片和基本信息 */}
          <div className="lg:col-span-2">
            {/* 主图 */}
            <div className="bg-white rounded-xl overflow-hidden shadow-lg mb-4">
              {activeImage ? (
                <img
                  src={activeImage}
                  alt={language === 'zh' ? project.name_zh : project.name_vi}
                  className="w-full h-96 object-cover"
                />
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-blue-100 to-gray-100 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-6xl mb-4 opacity-20">🏗️</div>
                    <span className={cn('text-sm font-semibold px-3 py-1 rounded-full', cat.color)}>
                      {categoryLabel}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* 图集缩略图 */}
            {galleryImages.length > 1 && (
              <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(img)}
                    className={cn(
                      'flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all',
                      activeImage === img ? 'border-blue-600' : 'border-transparent hover:border-blue-300'
                    )}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* 项目描述 */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                {language === 'zh' ? '项目描述' : 'Mô tả dự án'}
              </h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {language === 'zh' ? project.description_zh : project.description_vi}
              </p>
            </div>
          </div>

          {/* 右侧：项目信息卡片 */}
          <div className="lg:col-span-1 space-y-6">
            {/* 项目标题卡 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="mb-3">
                <span className={cn('inline-flex items-center px-3 py-1 rounded-full text-xs font-medium', cat.color)}>
                  {categoryLabel}
                </span>
                {project.is_featured && (
                  <span className="ml-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-600 text-white">
                    {language === 'zh' ? '精选' : 'Nổi bật'}
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                {language === 'zh' ? project.name_zh : project.name_vi}
              </h1>
              {location && (
                <div className="flex items-center text-gray-500 text-sm">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  {location}
                </div>
              )}
            </div>

            {/* 项目详情 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">
                {language === 'zh' ? '项目信息' : 'Thông tin dự án'}
              </h3>
              <dl className="space-y-3">
                {year && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 text-sm">{language === 'zh' ? '完工年份' : 'Năm hoàn thành'}</dt>
                    <dd className="font-medium text-gray-800 text-sm">{year}</dd>
                  </div>
                )}
                {project.start_date && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 text-sm">{language === 'zh' ? '开工日期' : 'Ngày bắt đầu'}</dt>
                    <dd className="font-medium text-gray-800 text-sm">{project.start_date}</dd>
                  </div>
                )}
                {project.end_date && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 text-sm">{language === 'zh' ? '竣工日期' : 'Ngày hoàn thành'}</dt>
                    <dd className="font-medium text-gray-800 text-sm">{project.end_date}</dd>
                  </div>
                )}
                {project.area_size && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 text-sm">{language === 'zh' ? '面积' : 'Diện tích'}</dt>
                    <dd className="font-medium text-gray-800 text-sm">{project.area_size} m²</dd>
                  </div>
                )}
                {project.client_name_zh && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 text-sm">{language === 'zh' ? '客户' : 'Khách hàng'}</dt>
                    <dd className="font-medium text-gray-800 text-sm">
                      {language === 'zh' ? project.client_name_zh : (project.client_name_vi || project.client_name_zh)}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-gray-500 text-sm">{language === 'zh' ? '状态' : 'Trạng thái'}</dt>
                  <dd>
                    <span className="text-xs font-semibold px-2 py-1 bg-green-100 text-green-800 rounded-full">
                      {language === 'zh' ? '已完工' : 'Đã hoàn thành'}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>

            {/* CTA */}
            <div className="bg-blue-600 rounded-xl p-6 text-white">
              <h3 className="text-lg font-bold mb-2">
                {language === 'zh' ? '有类似项目需求？' : 'Có nhu cầu tương tự?'}
              </h3>
              <p className="text-blue-100 text-sm mb-4">
                {language === 'zh'
                  ? '联系我们，获取免费项目咨询和报价'
                  : 'Liên hệ để nhận tư vấn và báo giá miễn phí'}
              </p>
              <Link
                to="/contact"
                className="block text-center py-2 px-4 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
              >
                {language === 'zh' ? '联系我们' : 'Liên hệ ngay'}
              </Link>
            </div>

            {/* 返回按钮 */}
            <button
              onClick={() => navigate(-1)}
              className="w-full py-3 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors"
            >
              {language === 'zh' ? '← 返回项目列表' : '← Quay lại danh sách'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailPage;
